import { useLingui } from '@lingui/react/macro';
import { CircleAlert } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { cn } from './cn';
import { useFieldConfig } from './field-config';
import { Label } from './label';

/** Props de acessibilidade que o `FormField` entrega ao controle. */
export interface FieldControlProps {
  readonly id: string;
  readonly name: string;
  readonly required: boolean;
  readonly disabled: boolean;
  readonly readOnly: boolean;
  readonly 'aria-labelledby': string;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: true;
  readonly 'aria-required'?: true;
}

export interface FormFieldProps {
  /** Nome do campo no formulário e chave no `FORM_FIELDS` do tenant. */
  readonly name: string;
  readonly label: ReactNode;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly hint?: ReactNode;
  /** Mensagem de erro (zod ou API por campo). Presente = campo inválido. */
  readonly error?: ReactNode;
  /** `inline` põe o controle antes do rótulo, para checkbox e switch. */
  readonly orientation?: 'stacked' | 'inline';
  /** Esconde o rótulo visualmente, mantendo-o para leitor de tela. */
  readonly hideLabel?: boolean;
  /** Ao lado do rótulo, fora do nome acessível do controle (o selo "Luy" do preenchimento, U56). */
  readonly adornment?: ReactNode;
  readonly className?: string;
  readonly children: (control: FieldControlProps) => ReactNode;
}

/**
 * Rótulo, obrigatório, dica e erro de um campo, resolvidos uma vez (discovery/05 §2.1). Aplica o
 * `FORM_FIELDS` do tenant: rótulo, oculto, desabilitado e obrigatório.
 */
export function FormField({
  name,
  label,
  required,
  disabled = false,
  readOnly = false,
  hint,
  error,
  orientation = 'stacked',
  hideLabel = false,
  adornment,
  className,
  children,
}: FormFieldProps) {
  const { t } = useLingui();
  const config = useFieldConfig(name);
  const id = useId();
  if (config.hidden) return null;

  const isRequired = config.required ?? required ?? false;
  const invalid = error !== undefined && error !== null && error !== false && error !== '';
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = invalid ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ');

  const control = children({
    id,
    name,
    required: isRequired,
    disabled: disabled || config.disabled,
    readOnly,
    'aria-labelledby': labelId,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(invalid ? { 'aria-invalid': true as const } : {}),
    ...(isRequired ? { 'aria-required': true as const } : {}),
  });

  const labelText = (
    <Label id={labelId} htmlFor={id} className={cn(hideLabel && 'sr-only')}>
      {config.label ?? label}
      {isRequired ? (
        <>
          <span aria-hidden className="ml-0.5 text-err">
            *
          </span>
          <span className="sr-only">{t`(obrigatório)`}</span>
        </>
      ) : null}
    </Label>
  );
  const labelNode = adornment ? (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {labelText}
      {adornment}
    </div>
  ) : (
    labelText
  );

  return (
    <div
      data-slot="form-field"
      data-invalid={invalid || undefined}
      className={cn('flex min-w-0 flex-col gap-1.5', className)}
    >
      {orientation === 'inline' ? (
        <div className="flex items-center gap-2.5">
          {control}
          {labelNode}
        </div>
      ) : (
        <>
          {labelNode}
          {control}
        </>
      )}
      {invalid ? (
        <p id={errorId} className="flex items-center gap-1 text-xs text-err">
          <CircleAlert aria-hidden className="size-3.5 shrink-0" />
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
